import PodcastString from '@pinecast/common/PodcastString';
import {BlockSkeleton, InlineSkeleton} from '@pinecast/common/Skeleton';
import type {DonationPage_podcast} from '@pinecast/gql/__generated_tj__/DonationPage';
import type {DonationSuccessPage_podcast} from '@pinecast/gql/__generated_tj__/DonationSuccessPage';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  podcast: DonationPage_podcast | DonationSuccessPage_podcast | null;
};

const DonationPageHeader = ({podcast}: Props) => {
  const css = useCSS();
  const designColors = useColors();

  const artwork = css({
    borderRadius: '10px',
    border: `1px solid ${designColors.borderGrayNormal}`,
    height: '144px',
    width: '144px',

    '@media (max-width: 600px)': {
      height: '112px',
      width: '112px',
    },
  });

  return (
    <div
      className={css({
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        padding: '8px 0 4px',
        textAlign: 'center',
      })}
    >
      {podcast ? (
        <img
          alt=""
          aria-hidden="true"
          className={artwork}
          src={podcast.coverArtThumbnail}
        />
      ) : (
        <BlockSkeleton
          style={{borderRadius: '10px', height: '144px', width: '144px'}}
        />
      )}
      <h1
        className={css({
          // Ginto is a Latin display face; shows named in a script it does not
          // cover fall back to the heading face rather than to a system serif.
          fontFamily:
            '"Ginto Nord Condensed", "Monument Grotesk Bold", sans-serif',
          fontSize: '46px',
          fontWeight: 400,
          letterSpacing: '0.02em',
          lineHeight: '50px',
          margin: 0,
          // A transform rather than capitalised copy, so a screen reader still
          // announces the show's name the way its owner wrote it. The name
          // carries the show's language, which also picks the casing rules.
          textTransform: 'uppercase',
          textWrap: 'balance',

          '@media (max-width: 600px)': {
            fontSize: '34px',
            lineHeight: '38px',
          },
        })}
      >
        {podcast ? (
          <FormattedMessage
            description="Title of the donation page. {podcastName} is the name of the podcast"
            defaultMessage="Support {podcastName}"
            values={{
              podcastName: (
                <PodcastString podcast={podcast}>{podcast.name}</PodcastString>
              ),
            }}
          />
        ) : (
          <InlineSkeleton style={{maxWidth: '300px'}} width="300px" />
        )}
      </h1>
      <p
        className={css({
          color: designColors.contentTextMuted,
          fontSize: '15px',
          lineHeight: '22px',
          margin: 0,
          maxWidth: '52ch',
          textWrap: 'pretty',
        })}
      >
        {podcast ? (
          podcast.hasPrivateEpisodes ? (
            <FormattedMessage
              description="Note that the podcast has private episodes"
              defaultMessage="Tip this show to access its private episodes and support great podcasting."
            />
          ) : (
            <FormattedMessage
              description="Generic message encouraging you to donate"
              defaultMessage="Contributing to your favorite podcasts helps them grow and improve."
            />
          )
        ) : (
          <InlineSkeleton width="260px" />
        )}
      </p>
    </div>
  );
};

export default React.memo(DonationPageHeader);
